import { LockOutlined } from '@ant-design/icons';
import { Button, Card, Form, Input, Space, Spin } from 'antd';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { type RootState } from '../../reducers';
import {
  ADMIN_PASSWORD_COMPLEXITY_REGEX,
  ADMIN_PASSWORD_MAX_LENGTH,
  ADMIN_PASSWORD_MIN_LENGTH,
} from '../../reducers/user/password-policy';
import { changePassword } from '../../reducers/user/reducer';
import { SPACING } from '../../theme/tokens';

interface IChangePasswordForm {
  currentPassword: string;
  newPassword: string;
  newPasswordConfirm: string;
}

function ChangePasswordForm(): React.ReactElement {
  const dispatch = useAppDispatch();
  const userInProcess = useSelector((s: RootState) => s.User.inProcess);
  const userPasswordChanged = useSelector(
    (s: RootState) => s.User.passwordChanged,
  );

  const [form] = Form.useForm<IChangePasswordForm>();

  useEffect(() => {
    if (userPasswordChanged) {
      form.resetFields();
    }
  }, [form, userPasswordChanged]);

  return (
    <Card size="small" title="Change password">
      <Form
        form={form}
        layout="vertical"
        onFinish={({
          currentPassword,
          newPassword,
        }: IChangePasswordForm): void => {
          dispatch(changePassword(currentPassword, newPassword));
        }}
      >
        <Form.Item
          name="currentPassword"
          label="Current password"
          rules={[{ required: true }]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            autoComplete="current-password"
            disabled={userInProcess}
          />
        </Form.Item>

        <Form.Item
          name="newPassword"
          label="New password"
          extra={`${ADMIN_PASSWORD_MIN_LENGTH}+ characters with a lowercase letter, an uppercase letter, a digit and a special character.`}
          rules={[
            { required: true },
            {
              type: 'string',
              min: ADMIN_PASSWORD_MIN_LENGTH,
              max: ADMIN_PASSWORD_MAX_LENGTH,
            },
            {
              pattern: ADMIN_PASSWORD_COMPLEXITY_REGEX,
              message:
                'Use a lowercase letter, an uppercase letter, a digit and a special character.',
            },
            ({ getFieldValue }) => ({
              validator(_, value: string | undefined): Promise<void> {
                if (!value || getFieldValue('currentPassword') !== value) {
                  return Promise.resolve();
                }

                return Promise.reject(
                  new Error('New password must differ from the current one.'),
                );
              },
            }),
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            autoComplete="new-password"
            disabled={userInProcess}
          />
        </Form.Item>

        <Form.Item
          name="newPasswordConfirm"
          label="Confirm new password"
          dependencies={['newPassword']}
          rules={[
            { required: true },
            ({ getFieldValue }) => ({
              validator(_, value: string | undefined): Promise<void> {
                if (!value || getFieldValue('newPassword') === value) {
                  return Promise.resolve();
                }

                return Promise.reject(new Error('Passwords do not match.'));
              },
            }),
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            autoComplete="new-password"
            disabled={userInProcess}
          />
        </Form.Item>

        <Space size={SPACING.lg} align="center">
          <Button type="primary" htmlType="submit" disabled={userInProcess}>
            Change password
          </Button>
          <Spin size="small" spinning={userInProcess} />
        </Space>
      </Form>
    </Card>
  );
}

export default ChangePasswordForm;
